import { Account } from '@/types'
import { Mail, Phone } from 'lucide-react'
import { router } from '@inertiajs/react'

interface AccountCardProps {
  account: Account
  organization: {
    slug: string
  }
}

export default function AccountCard({ account, organization }: AccountCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getTierBadgeStyles = (tier: string | null) => {
    return tier === 'Tier 1' 
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
      : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
  }

  const getCompanyInitials = (name: string) => {
    return name
      .split(' ')
      .slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join('')
  }

  const getGradientFromName = (name: string) => {
    const gradients = [
      'from-blue-500 to-blue-600',
      'from-purple-500 to-purple-600', 
      'from-green-500 to-green-600',
      'from-orange-500 to-orange-600',
      'from-pink-500 to-pink-600',
      'from-indigo-500 to-indigo-600',
      'from-teal-500 to-teal-600',
      'from-red-500 to-red-600'
    ]
    
    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    
    return gradients[Math.abs(hash) % gradients.length]
  }

  const CardHeader = () => (
    <div className="p-6 pb-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 bg-gradient-to-br ${!account.logo ? getGradientFromName(account.name) : 'from-gray-100 to-gray-200'} rounded-lg flex items-center justify-center shadow-sm`}>
            {account.logo && account.logo.trim() !== '' ? (
              <img src={account.logo} alt={`${account.name} Logo`} className="w-8 h-8 object-contain" />
            ) : (
              <span className="text-white font-semibold text-sm">
                {getCompanyInitials(account.name)}
              </span>
            )}
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors">
              {account.name}
            </h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {account.industry}
              </span>
            </div>
          </div>
        </div>
      </div>

      <span className={`inline-flex px-2 py-1 mb-4 text-xs font-medium rounded-full ${getTierBadgeStyles(account.tier)}`}>
        {account.tier}
      </span>

      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
        {account.description}
      </p>
    </div>
  )

  const MetricsSection = () => (
    <div className="flex-1 px-6 pb-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Opportunities</p>
          <div className="flex items-center justify-center space-x-1">
            <span className="text-lg font-extrabold text-gray-900 dark:text-white">
              {account.opportunities.count}
            </span>
          </div>
          {account.opportunities.newCount > 0 && (
            <span className="inline-flex px-1.5 py-0.5 mt-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300 rounded-full">
              +{account.opportunities.newCount} new
            </span>
          )}
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Deal Value</p>
          <div className="flex items-center justify-center space-x-1">
            <span className="text-lg font-extrabold text-gray-900 dark:text-white">
              {formatCurrency(account.opportunities.totalValue)}
            </span>
          </div>
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Last Activity</p>
          <div className="flex items-center justify-center space-x-1">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {formatDate(account.lastActivity)}
            </span>
          </div>
        </div>
      </div>
    </div>
  )

  const ContactFooter = () => (
    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {account.contactPerson.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {account.contactPerson.title}
          </p>
        </div>
        <div className="flex space-x-2">
          <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors" title="Call">
            <Phone className="w-4 h-4 text-gray-400 hover:text-blue-600" />
          </button>
          <button className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors" title="Email">
            <Mail className="w-4 h-4 text-gray-400 hover:text-blue-600" />
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div 
      onClick={() => router.visit(`/~/${organization.slug}/accounts/${account.id}`)}
      className="flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
    >
      <div className="flex-1 flex flex-col">
        <CardHeader />
        <MetricsSection />
      </div>
      <ContactFooter />
    </div>
  )
}